/* eslint-disable arrow-body-style */

import { getExternalAddOnsZipCode, getSelectedExternalAddOns } from '../selectors/externalAddOns';
import * as types from './types';
import fetchData from '../../helpers/fetchData';
import fetchOptions from '../../helpers/fetchOptions';

/**
 * Function to validate the zip code and return error message if any
 * @param {string} zipCode - zip code
 * @returns {Object} - error object
 */
export const checkZipCode = function checkZipCode(zipCode) {
  const result = {
    error: false,
    errorMessage: '',
  };

  if (!zipCode) {
    result.error = true;
    result.errorMessage = 'Zip code is required';
  } else if (!zipCode.match(/(^\d{5}$)/)) {
    result.error = true;
    result.errorMessage = 'Zip code is not valid';
  }

  return result;
}

/**
 * Handler function for external add-ons API response
 * @param {Object} externalAddOnsResult - external add-ons response
 * @param {string} productId - product ID
 * @returns {Object} - action object
 */
export const receiveExternalAddOns = (externalAddOnsResult, productId) => ({
  type: types.RECEIVE_EXTERNAL_ADDONS,
  externalAddOns: externalAddOnsResult,
  productId,
});

export const receiveExternalAddOnsBulk = externalAddOnsResult => ({
  type: types.RECEIVE_EXTERNAL_ADDONS_BULK,
  externalAddOns: externalAddOnsResult,
});

export const resetExternalAddOns = (productId) => ({
  type: types.RESET_EXTERNAL_ADDONS,
  productId,
});

/**
 * Function to update the zip code and call API is zip code is valid
 * @param {string} zipCode - zip code
 * @param {boolean} shouldSaveZipCode - flag to save zip code
 * @param {Function} getLargeApplianceAttributes - callback to get large appliance attributes
 * @returns {Funtion} - dispatch function
 */
export const updateZipCode = (zipCode, getLargeApplianceAttributes) => {

  return (dispatch, getState) => {
    const {
      error,
      errorMessage,
    } = checkZipCode(zipCode);
    const { id } = getLargeApplianceAttributes(getState());

    dispatch({
      type: types.UPDATE_ZIPCDOE,
      zipCode,
      error,
      errorMessage,
      productId: id,
    });
  };
};

/**
 * Action to get the external add-ons
 * @param {String} zipCode - zip code
 * @param {Boolean} shouldSaveZipCode - flag to save zip code
 * @param {Function} getLargeApplianceAttributes - callback to get large appliance attributes
 */
export const fetchExternalAddOnsAction = (zipCode, shouldSaveZipCode, getLargeApplianceAttributes) => {
  return (dispatch, getState) => {
    const {
      error,
    } = checkZipCode(zipCode);
    const { getExternalAddOnsUrl, id } = getLargeApplianceAttributes(getState());

    if (!error) {
        dispatch(fetchExternalAddOns(getExternalAddOnsUrl, zipCode, id, shouldSaveZipCode));
    }
  }
};

/**
 * Function to update the selected add-on
 * @param {string} addOnID - add-on ID
 * @param {boolean} selected - selected flag
 * @param {Function} getLargeApplianceAttributes - callback to get large appliance attributes
 * @returns {Function} - dispatch function
 */
export const updateAddOnSelection = (addOnID, selected, getLargeApplianceAttributes) => {
  return (dispatch, getState) => {
    const { getExternalAddOnsUrl, id } = getLargeApplianceAttributes(getState());

    dispatch({
      type: types.SET_ADD_ON_SELECTION,
      addOnID,
      selected,
      productId: id,
    });

    const selectedAddOns = getSelectedExternalAddOns(id)(getState()) || [];
    const zipCode = getExternalAddOnsZipCode(id)(getState());

    // as user has already saved the zip code
    const shouldSaveZipCode = false;

    dispatch(
      fetchExternalAddOns(
        getExternalAddOnsUrl,
        zipCode,
        id,
        shouldSaveZipCode,
        selectedAddOns.join(','),
      )
    );
  };
};

/**
 * Function to update the selected add-on for the cart page
 * @param {string} addOnID - add-on ID
 * @param {boolean} selected - selected flag
 * @param {Function} getLargeApplianceAttributes - callback to get large appliance attributes
 * @returns {Function} - dispatch function
 */
export const updateAddOnSelectionForCart = (addOnID, selected, getLargeApplianceAttributes) => {
  return (dispatch, getState) => {
    const { updateExternalAddOnsUrl, id: productId,  uuid } = getLargeApplianceAttributes(getState());

    dispatch({
      type: types.SET_ADD_ON_SELECTION,
      addOnID,
      selected,
      productId: uuid
    });

    const selectedAddOns = getSelectedExternalAddOns(uuid)(getState()) || [];

    dispatch(updateExternalAddOnsSelection(updateExternalAddOnsUrl, productId, selectedAddOns.join(','), uuid));
  }
};

/**
 * 
 * @param {Boolean} inFlight - inFlight flag
 * @param {String} productId - product ID
 * @returns {Object} - action object
 */
export const setInFlight = (inFlight, productId) => ({
  type: types.SET_ADD_ON_IN_FLIGHT,
  inFlight,
  productId,
});

/**
 * Function to update the selected add-on for the cart page
 * @param {string} newDeliveryDate - new Delivery Date
 * @param {Function} getLargeApplianceAttributes - callback to get large appliance attributes
 * @returns {Function} - dispatch function
 */
export const updateDeliveryDateForCart = (newDeliveryDate, getLargeApplianceAttributes) => {
  return (dispatch, getState) => {
    const { updateDeliveryDateUrl, id: productId, uuid } = getLargeApplianceAttributes(getState());

    dispatch({
      type: types.UPDATE_DELIVERY_DATE,
      newDeliveryDate,
      productId: uuid,
    });

    dispatch(updateDeliveryDate(updateDeliveryDateUrl, productId, newDeliveryDate, uuid));
  };
};

/**
 * Update cart with the response from external add-ons API
 * @param {Object} cart - cart object
 * @returns 
 */
export const receiveCart = (cart) => {
  if (cart.redirectUrl) {
    window.location.assign(cart.redirectUrl);
    return {};
  }

  return {
    type: types.RECEIVE_CART_RESULTS,
    cart,
  };
};

/**
 * Action to fetch external add-ons
 * @param {string} url - url to fetch external add-ons
 * @param {string} zipCode - zip code to fetch external add-ons
 * @param {string} productID - product ID to fetch external add-ons
 * @param {boolean} shouldSaveZipCode - flag to save zip code
 * @param {string} deliveryServiceIds - comma separated delivery service ids selected by user
 * @returns {Function} - dispatch function
 */
export const fetchExternalAddOns = function fetchExternalAddOnsAction(url, zipCode, productID, shouldSaveZipCode, deliveryServiceIds = '') {
  return function requestExternalAddOns(dispatch) {
    dispatch(setInFlight(true, productID));
    let getExternalAddOnsUrl = `${url}?zipcode=${zipCode}&pid=${productID}&shouldSaveZipCode=${shouldSaveZipCode}`;

    if (deliveryServiceIds) {
      getExternalAddOnsUrl = `${getExternalAddOnsUrl}&deliveryServiceIds=${deliveryServiceIds}`;
    }
    return fetchData(getExternalAddOnsUrl,
      fetchOptions('get'),
      {
        successHandler: response => receiveExternalAddOns(response, productID),
        dispatch,
      });
  };
}

/**
 * Update the external add ons selection for the cart page 
 * @param {String} url - url to update external add ons selection
 * @param {String} productID - product ID
 * @param {String} deliveryServiceIds - comma separated delivery service ids selected by user
 * @param {String} uuid - uuid for product line item
 * @returns 
 */
export const updateExternalAddOnsSelection = function updateExternalAddOnsSelectionAction(url, productID, deliveryServiceIds = '', uuid) {
  return function requestExternalAddOns(dispatch) {
    const payload = {
      pid: productID,
      deliveryServiceIds,
      uuid
    }
    return fetchData(url,
      fetchOptions('postForm', payload),
      {
        successHandler: (response) => [receiveCart(response), receiveExternalAddOnsBulk(response.externalAddOns)],
        dispatch: (successActions = []) => successActions.forEach(action => dispatch(action)),
      },
    );
  };
}

/**
 * Update the external add ons selection for the cart page
 * @param {string} url - url to update external add ons selection
 * @param {string} productID - product ID
 * @param {string} newDeliveryDate - new Delivery date selected by the user
 * @param {string} uuid - uuid for product line item
 * @returns {Function} - fetch data post call
 */
export const updateDeliveryDate = function updateDeliveryDateAction(
  url,
  productID,
  newDeliveryDate,
  uuid,
) {
  return function requestExternalAddOns(dispatch) {
    const payload = {
      pid: productID,
      deliveryDate: newDeliveryDate,
      uuid,
    };

    return fetchData(url,
      fetchOptions('postForm', payload),
      {
        successHandler: receiveCart,
        dispatch,
      });
  };
};

/**
 * Update the zip code for the cart page 
 * @param {String} url - url to update external add ons selection
 * @param {String} zipCode - zip code
 * @returns 
 */
export const updateZipCodeInCart = (url,zipCode) => {
  return (dispatch) => {

    let updateZipCodeUrl = `${url}?zipCode=${zipCode}`;
      return fetchData(updateZipCodeUrl,
        fetchOptions('get'),
        {
          successHandler: (response) => [receiveCart(response), receiveExternalAddOnsBulk(response.externalAddOns)],
          dispatch: (successActions = []) => successActions.forEach(action => dispatch(action)),
        },
      );
    }
}
